// ThreeJs/Segments/SegmentPastePreview.js
import * as THREE from "three";
import { VARS } from "../Global/variables";
import { sceneManager } from "../SetUps/SceneManager";

// Internal state for the ghost preview
let previewActive = false;
let ghostLine = null;           // Line for the dotted outline
let cachedVertexCount = 0;

// Shared raycaster for preview
const previewRaycaster = new THREE.Raycaster();

/**
 * Get the geometric center of an array of points.
 */
function getPointsCenter(points) {
  const center = new THREE.Vector3();
  points.forEach((p) => center.add(p));
  if (points.length > 0) center.divideScalar(points.length);
  return center;
}

/**
 * Build a new frame on the target plane:
 *   - n1 = target normal
 *   - u1 = projection of original tangent u0 into target plane
 *   - v1 = n1 × u1
 */
function buildTargetFrame(fromTangent, targetNormal) {
  const n1 = targetNormal.clone().normalize();
  let u1 = fromTangent.clone();

  // Project original tangent into target plane
  u1.sub(n1.clone().multiplyScalar(u1.dot(n1)));

  if (u1.lengthSq() < 1e-8) {
    u1.set(1, 0, 0);
    if (Math.abs(u1.dot(n1)) > 0.9) u1.set(0, 1, 0);
    u1.sub(n1.clone().multiplyScalar(u1.dot(n1)));
  }

  u1.normalize();
  const v1 = new THREE.Vector3().crossVectors(n1, u1).normalize();

  return { u1, v1, n1 };
}

/**
 * Rebuild world positions from stored UV coordinates in a new frame.
 */
function rebuildPointsFromUV(copiedData, targetNormal, pasteCenter) {
  const { uv, tangent } = copiedData;
  const { u1, v1, n1 } = buildTargetFrame(tangent, targetNormal);

  const base = pasteCenter.clone();
  const points = uv.map(({ u, v }) =>
    base.clone().add(
      u1.clone().multiplyScalar(u)
        .add(v1.clone().multiplyScalar(v))
    )
  );

  return { points, normal: n1 };
}

/**
 * Create the ghost line object (dotted polygon outline) and add to scene.
 */
function createGhostLine(points) {
  const scene = sceneManager.scene;

  const closed = [...points, points[0].clone()]; // close loop

  const geometry = new THREE.BufferGeometry().setFromPoints(closed);

  const material = new THREE.LineDashedMaterial({
    color: 0xffffff,
    dashSize: 0.4,
    gapSize: 0.25,
    opacity: 0.9,
    transparent: true,
    depthWrite: false,
  });

  const line = new THREE.Line(geometry, material);
  line.computeLineDistances();
  line.renderOrder = 10; // draw above roof

  scene.add(line);
  return line;
}

/**
 * Update the ghost line geometry in-place with new points.
 */
function updateGhostLine(points) {
  if (!ghostLine) return;

  const closed = [...points, points[0].clone()];
  const positionAttr = ghostLine.geometry.getAttribute("position");

  if (!positionAttr || positionAttr.count !== closed.length) {
    ghostLine.geometry.dispose();
    ghostLine.geometry = new THREE.BufferGeometry().setFromPoints(closed);
  } else {
    const arr = positionAttr.array;
    for (let i = 0; i < closed.length; i++) {
      const p = closed[i];
      const base = i * 3;
      arr[base] = p.x;
      arr[base + 1] = p.y;
      arr[base + 2] = p.z;
    }
    positionAttr.needsUpdate = true;
  }

  ghostLine.computeLineDistances();
  ghostLine.visible = true;
}

/**
 * Remove and dispose the ghost line from the scene.
 */
function disposeGhostLine() {
  if (!ghostLine) return;
  const scene = sceneManager.scene;
  scene.remove(ghostLine);
  ghostLine.geometry.dispose();
  ghostLine.material.dispose();
  ghostLine = null;
  cachedVertexCount = 0;
}

/**
 * Temporarily hide the ghost line without disposing it.
 */
export function hideSegmentPasteGhost() {
  if (ghostLine) {
    ghostLine.visible = false;
  }
}

/**
 * Enable preview mode. Called after copying a segment.
 */
export function enableSegmentPastePreview() {
  const data = VARS.copiedSegment;
  if (!data || !data.uv || !data.uv.length) return;
  previewActive = true;
}

/**
 * Disable preview mode and remove the ghost from the scene.
 */
export function disableSegmentPastePreview() {
  previewActive = false;
  disposeGhostLine();
}

/**
 * Main update function:
 *  1. Raycast to roof.
 *  2. Rebuild from UVs in the target frame.
 *  3. Drop rigidly along normal.
 *  4. Update/create dashed ghost polygon.
 */
export function updateSegmentPastePreview() {
  if (!previewActive) return;

  const data = VARS.copiedSegment;
  if (!data || !data.uv || !data.uv.length) {
    disableSegmentPastePreview();
    return;
  }

  const meshes = VARS.Meshes || [];
  if (!meshes.length) {
    disposeGhostLine();
    return;
  }

  const ndc = VARS.mouseNDC;
  if (!ndc) {
    return;
  }

  // 1️⃣ Raycast to roof
  previewRaycaster.setFromCamera(
    { x: ndc.x, y: ndc.y },
    sceneManager.camera
  );
  const hits = previewRaycaster.intersectObjects(meshes, true);

  if (!hits.length) {
    if (ghostLine) ghostLine.visible = false;
    return;
  }

  const hit = hits[0];
  const pasteCenter = hit.point.clone();

  let targetNormal;
  if (hit.face) {
    targetNormal = hit.face.normal
      .clone()
      .transformDirection(hit.object.matrixWorld);
    targetNormal.normalize();
  } else {
    // fallback: preserve original normal
    targetNormal = data.normal
      ? data.normal.clone().normalize()
      : new THREE.Vector3(0, 0, 1);
  }

  // 2️⃣ Rebuild points from UVs in the new frame
  const { points: rebuiltPoints, normal: finalNormal } = rebuildPointsFromUV(
    data,
    targetNormal,
    pasteCenter
  );

  // 3️⃣ Drop center rigidly along normal
  const rigidCenter = getPointsCenter(rebuiltPoints);
  let finalPoints = rebuiltPoints;

  if (meshes.length) {
    const dropRay = new THREE.Raycaster();
    const dropOrigin = rigidCenter.clone().add(finalNormal.clone().multiplyScalar(2));
    dropRay.set(dropOrigin, finalNormal.clone().negate());

    const dropHits = dropRay.intersectObjects(meshes, true);
    if (dropHits.length) {
      const centerHit = dropHits[0].point.clone();
      const delta = centerHit.clone().sub(rigidCenter);

      finalPoints = rebuiltPoints.map((p) => p.clone().add(delta));
    }
  }

  // 4️⃣ Create or update dashed ghost polygon
  if (!ghostLine || cachedVertexCount !== finalPoints.length) {
    disposeGhostLine();
    ghostLine = createGhostLine(finalPoints);
    cachedVertexCount = finalPoints.length;
  } else {
    updateGhostLine(finalPoints);
  }
}
